import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  clampSeekTime,
  computeWaveformPeaks,
  formatClock,
  formatRemaining,
} from "../../utils/results/manualAudioPlayback";

const WAVEFORM_BARS = 56;
const SKIP_SECONDS = 5;

/* ------------------------------------------------------------------ */
/* Inline control glyphs (kept local — they only exist for this player) */
/* ------------------------------------------------------------------ */

function PlayGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
    </svg>
  );
}

function PauseGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <rect x="6" y="5" width="4" height="14" rx="1.2" />
      <rect x="14" y="5" width="4" height="14" rx="1.2" />
    </svg>
  );
}

function WaveformGlyph(props) {
  // Simple amplitude bars — the "show waveform" toggle affordance.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      {...props}
    >
      <line x1="4" y1="10" x2="4" y2="14" />
      <line x1="8" y1="6" x2="8" y2="18" />
      <line x1="12" y1="9" x2="12" y2="15" />
      <line x1="16" y1="4" x2="16" y2="20" />
      <line x1="20" y1="8" x2="20" y2="16" />
    </svg>
  );
}

// Google Material Icons "replay_5" / "forward_5" (Apache 2.0). The "5" digit
// is part of the glyph, so the skip controls need no text overlay.
function Replay5Glyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12,5V1L7,6l5,5V7c3.31,0,6,2.69,6,6s-2.69,6-6,6s-6-2.69-6-6H4c0,4.42,3.58,8,8,8s8-3.58,8-8S16.42,5,12,5z" />
      <path d="M10.69,13.9l0.25-2.17h2.39v0.71h-1.7l-0.11,0.92c0.03-0.02,0.07-0.03,0.11-0.05s0.09-0.04,0.15-0.05s0.12-0.03,0.18-0.04s0.13-0.02,0.2-0.02c0.21,0,0.39,0.03,0.55,0.1s0.3,0.16,0.41,0.28s0.2,0.27,0.25,0.45s0.09,0.38,0.09,0.6c0,0.19-0.03,0.37-0.09,0.54s-0.15,0.32-0.27,0.45s-0.27,0.24-0.45,0.31s-0.39,0.12-0.64,0.12c-0.18,0-0.36-0.03-0.53-0.08s-0.32-0.14-0.46-0.24s-0.24-0.24-0.32-0.39s-0.13-0.33-0.13-0.53h0.84c0.02,0.18,0.08,0.32,0.19,0.41s0.25,0.15,0.42,0.15c0.11,0,0.2-0.02,0.27-0.06s0.14-0.1,0.18-0.17s0.08-0.15,0.11-0.25s0.03-0.2,0.03-0.31s-0.01-0.21-0.04-0.31s-0.07-0.17-0.13-0.24s-0.13-0.12-0.21-0.15s-0.19-0.05-0.3-0.05c-0.08,0-0.15,0.01-0.2,0.02s-0.11,0.03-0.15,0.05s-0.08,0.05-0.12,0.07s-0.07,0.06-0.1,0.09L10.69,13.9z" />
    </svg>
  );
}

function Forward5Glyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18,13c0,3.31-2.69,6-6,6s-6-2.69-6-6s2.69-6,6-6v4l5-5l-5-5v4c-4.42,0-8,3.58-8,8c0,4.42,3.58,8,8,8c4.42,0,8-3.58,8-8H18z" />
      <path d="M12.03,15.38c-0.44,0-0.58-0.31-0.6-0.56h-0.84c0.03,0.85,0.79,1.25,1.44,1.25c0.93,0,1.44-0.63,1.44-1.43c0-1.33-0.97-1.44-1.3-1.44c-0.2,0-0.43,0.05-0.64,0.16l0.11-0.92h1.7v-0.71h-2.39l-0.25,2.17l0.67,0.17c0.13-0.13,0.28-0.23,0.57-0.23c0.4,0,0.69,0.23,0.69,0.75C12.62,14.64,12.65,15.38,12.03,15.38z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */

function createAudioContext() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  return Ctor ? new Ctor() : null;
}

/**
 * Apple Voice Memos style audio player: a scrubbable progress/waveform track,
 * elapsed / remaining time, and skip-15 + play/pause transport controls.
 * Deliberately omits the delete (bin) control.
 */
function VoiceMemoPlayerImpl({ src, durationHint = null, className = "" }) {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const durationResolvedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationHint || 0);
  const [peaks, setPeaks] = useState(null);
  const [showWaveform, setShowWaveform] = useState(true);
  const [isScrubbing, setIsScrubbing] = useState(false);

  const effectiveDuration =
    Number.isFinite(duration) && duration > 0
      ? duration
      : durationHint || 0;
  const progress =
    effectiveDuration > 0
      ? Math.min(1, Math.max(0, currentTime / effectiveDuration))
      : 0;

  // Reset transport state whenever the source changes.
  useEffect(() => {
    durationResolvedRef.current = false;
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(durationHint || 0);
    setPeaks(null);
  }, [src, durationHint]);

  // Wire the media element's lifecycle events. WebM/Opus captures can report
  // an Infinity duration until the browser is nudged to seek to the end, so we
  // apply the well-known "seek to a huge time, then reset" workaround.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return undefined;

    const resolveDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        durationResolvedRef.current = true;
        setDuration(audio.duration);
      }
    };

    const handleLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        resolveDuration();
      } else if (!durationResolvedRef.current) {
        const handleDurationChange = () => {
          if (Number.isFinite(audio.duration) && audio.duration > 0) {
            audio.currentTime = 0;
            resolveDuration();
            audio.removeEventListener("durationchange", handleDurationChange);
          }
        };
        audio.addEventListener("durationchange", handleDurationChange);
        // Trigger the browser to compute the true duration.
        audio.currentTime = 1e101;
      }
    };

    const handleTimeUpdate = () => {
      if (!durationResolvedRef.current) return;
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, [src]);

  // Decode the clip to a waveform. This is a cross-origin fetch, so it only
  // succeeds when the storage bucket allows it; on any failure we silently keep
  // the plain progress bar rather than fabricating a waveform.
  useEffect(() => {
    if (!src || typeof fetch !== "function") return undefined;
    const context = createAudioContext();
    if (!context) return undefined;

    let cancelled = false;
    const controller =
      typeof AbortController === "function" ? new AbortController() : null;

    (async () => {
      try {
        const response = await fetch(src, { signal: controller?.signal });
        const buffer = await response.arrayBuffer();
        const audioBuffer = await context.decodeAudioData(buffer);
        if (cancelled) return;
        const channel = audioBuffer.getChannelData(0);
        setPeaks(computeWaveformPeaks(channel, WAVEFORM_BARS));
      } catch {
        // CORS / decode failure — fall back to the progress bar.
      } finally {
        if (context.state !== "closed") context.close?.();
      }
    })();

    return () => {
      cancelled = true;
      controller?.abort();
      if (context.state !== "closed") context.close?.();
    };
  }, [src]);

  const seekTo = useCallback(
    (seconds) => {
      const audio = audioRef.current;
      const target = clampSeekTime(seconds, effectiveDuration);
      setCurrentTime(target);
      if (audio) audio.currentTime = target;
    },
    [effectiveDuration],
  );

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      const played = audio.play();
      if (played && typeof played.catch === "function") {
        played.catch(() => setIsPlaying(false));
      }
    } else {
      audio.pause();
    }
  }, []);

  const skip = useCallback(
    (delta) => seekTo(currentTime + delta),
    [currentTime, seekTo],
  );

  const seekToClientX = useCallback(
    (clientX) => {
      const track = trackRef.current;
      if (!track || effectiveDuration <= 0) return;
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return;
      const fraction = Math.min(
        1,
        Math.max(0, (clientX - rect.left) / rect.width),
      );
      seekTo(fraction * effectiveDuration);
    },
    [effectiveDuration, seekTo],
  );

  const handlePointerDown = useCallback(
    (event) => {
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setIsScrubbing(true);
      seekToClientX(event.clientX);
    },
    [seekToClientX],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!isScrubbing) return;
      seekToClientX(event.clientX);
    },
    [isScrubbing, seekToClientX],
  );

  const endScrub = useCallback(() => setIsScrubbing(false), []);

  const handleTrackKeyDown = useCallback(
    (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        skip(-5);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        skip(5);
      } else if (event.key === "Home") {
        event.preventDefault();
        seekTo(0);
      } else if (event.key === "End") {
        event.preventDefault();
        seekTo(effectiveDuration);
      } else if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        togglePlay();
      }
    },
    [skip, seekTo, effectiveDuration, togglePlay],
  );

  const hasWaveform = Array.isArray(peaks) && peaks.length > 0;
  const waveformActive = showWaveform && hasWaveform;

  const waveformBars = useMemo(() => {
    if (!waveformActive) return null;
    const playedBars = Math.round(progress * peaks.length);
    return peaks.map((peak, index) => {
      const height = Math.max(8, Math.round(peak * 100));
      return (
        <span
          key={index}
          className={`flex-1 rounded-full transition-colors ${
            index < playedBars ? "bg-primary" : "bg-greyscale-1"
          }`}
          style={{ height: `${height}%` }}
        />
      );
    });
  }, [waveformActive, peaks, progress]);

  const playLabel = isPlaying
    ? t("result.manualAudio.player.pause", "Pause")
    : t("result.manualAudio.player.play", "Play");

  return (
    <div
      className={`w-full flex flex-col gap-3 rounded-2xl bg-card p-4 shadow-sm select-none ${className}`}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />

      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label={t("result.manualAudio.player.seek", "Seek")}
        aria-valuemin={0}
        aria-valuemax={Math.round(effectiveDuration)}
        aria-valuenow={Math.round(currentTime)}
        aria-valuetext={formatClock(currentTime)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endScrub}
        onPointerCancel={endScrub}
        onKeyDown={handleTrackKeyDown}
        className="w-full cursor-pointer touch-none outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-full"
      >
        {waveformActive ? (
          <div className="flex h-14 w-full items-center gap-[2px]">
            {waveformBars}
          </div>
        ) : (
          <div className="relative h-1.5 w-full rounded-full bg-greyscale-1">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}
      </div>

      <div className="flex w-full items-center justify-between text-xs font-medium text-greyscale-3 tabular-nums">
        <span>{formatClock(currentTime)}</span>
        <span>{formatRemaining(currentTime, effectiveDuration)}</span>
      </div>

      <div className="flex w-full items-center justify-between px-1">
        <button
          type="button"
          onClick={() => setShowWaveform((prev) => !prev)}
          disabled={!hasWaveform}
          aria-pressed={waveformActive}
          aria-label={t(
            "result.manualAudio.player.toggleWaveform",
            "Toggle waveform",
          )}
          className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors disabled:opacity-30 ${
            waveformActive ? "text-primary" : "text-greyscale-3"
          }`}
        >
          <WaveformGlyph className="h-6 w-6" />
        </button>

        <button
          type="button"
          onClick={() => skip(-SKIP_SECONDS)}
          aria-label={t("result.manualAudio.player.skipBack", "Skip back 5 seconds")}
          className="flex h-11 w-11 items-center justify-center rounded-full text-greyscale-3 transition-opacity hover:opacity-80"
        >
          <Replay5Glyph className="h-7 w-7" />
        </button>

        <button
          type="button"
          onClick={togglePlay}
          aria-label={playLabel}
          className="flex h-14 w-14 items-center justify-center rounded-full text-primary transition-opacity hover:opacity-80"
        >
          {isPlaying ? (
            <PauseGlyph className="h-9 w-9" />
          ) : (
            <PlayGlyph className="h-9 w-9" />
          )}
        </button>

        <button
          type="button"
          onClick={() => skip(SKIP_SECONDS)}
          aria-label={t(
            "result.manualAudio.player.skipForward",
            "Skip forward 5 seconds",
          )}
          className="flex h-11 w-11 items-center justify-center rounded-full text-greyscale-3 transition-opacity hover:opacity-80"
        >
          <Forward5Glyph className="h-7 w-7" />
        </button>

        {/* Spacer to keep the transport visually centred where Voice Memos
            places its delete control — which we intentionally omit. */}
        <span aria-hidden="true" className="h-11 w-11" />
      </div>
    </div>
  );
}

const VoiceMemoPlayer = memo(VoiceMemoPlayerImpl);

export default VoiceMemoPlayer;
